import {
  Alert,
  AlertProps,
  Snackbar,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { AnalyticsEvents, analytics } from "common/analytics";
import { setGlobalNotify } from "common/utils/notificationBus";
import { createContext, useContext, useEffect, useMemo, useState } from "react";

type ContextProps = {
  notify: (message: string, severity?: AlertProps["severity"]) => void;
};

const NotificationContext = createContext<ContextProps>({
  notify: () => undefined,
});
const { Provider } = NotificationContext;

export default function NotificationProvider({
  children,
}: {
  children?: React.ReactNode;
}) {
  const [notification, setNotification] = useState<Notification | null>(null);
  const [open, setOpen] = useState<boolean>(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const value = useMemo(() => ({ notify }), []);

  // Expose notify to non-React modules (the API client in common/utils/Api.tsx
  // uses it to warn the user when a write is attempted while offline).
  useEffect(() => {
    setGlobalNotify(notify);
    return () => setGlobalNotify(undefined);
  }, [notify]);

  return (
    <Provider value={value}>
      {children}
      {notification && (
        <Snackbar
          open={open}
          autoHideDuration={6000}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          onClose={close}
          disableWindowBlurListener
          sx={{
            // Clear BottomTabNavigation (70px + safe area, z-index 9999) and FAB (10000)
            bottom: isMobile
              ? "calc(70px + env(safe-area-inset-bottom) + 8px)"
              : "8px",
            zIndex: 10001,
          }}
        >
          <Alert
            elevation={6}
            variant="filled"
            severity={notification.severity}
            onClose={close}
          >
            {notification.message}
          </Alert>
        </Snackbar>
      )}
    </Provider>
  );

  function notify(message: string, severity?: AlertProps["severity"]) {
    setOpen(true);
    setNotification({ message, severity: severity || "success" });
    if (severity === "error") {
      analytics.track(AnalyticsEvents.ERROR_DISPLAYED, { message });
    }
  }

  function close(
    event: Event | React.SyntheticEvent<Element, Event>,
    reason?: string
  ) {
    if (reason === "clickaway") return;
    setOpen(false);
  }
}

export function useNotification() {
  return useContext(NotificationContext);
}

type Notification = {
  message: string;
  severity: AlertProps["severity"];
};
